import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, CircleDollarSign, AtSign, Flag, MessageSquare, CheckCircle2, PenLine } from 'lucide-react';
import { api } from '../api/sangly';
import { timeAgo } from '../lib/format';
import { cn } from '../lib/utils';

const KIND_ICON = {
  paid: CircleDollarSign,
  mention: AtSign,
  flagged: Flag,
  comment: MessageSquare,
  resolved: CheckCircle2,
  update: PenLine,
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try { setCount((await api.notifications.unreadCount()).count); } catch { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    try { setItems(await api.notifications.list()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000); // gentle background poll
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const openItem = async (n) => {
    if (!n.read) { try { await api.notifications.markRead(n.id); } catch { /* ignore */ } }
    setOpen(false);
    loadCount();
    if (n.proforma) navigate(`/proformas/${n.proforma}`);
    else if (n.refund) navigate('/refunds?tab=to_finalize');
    else if (n.supplier) navigate(`/suppliers/${n.supplier}`);
    else if (n.invoice) navigate(`/invoices/${n.invoice}`);
    else navigate('/notifications');
  };

  const markAll = async () => {
    try { await api.notifications.markAllRead(); } catch { /* ignore */ }
    setCount(0);
    loadList();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative grid h-9 w-9 place-items-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title="Notifications" aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground ring-2 ring-background">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-popover shadow-pop animate-pop-in">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[13px] font-semibold">Notifications</span>
            <button onClick={markAll} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-3 py-8 text-center text-[13px] text-muted-foreground">You're all caught up 🐗</div>
            )}
            {items.slice(0, 12).map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={cn('flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted',
                    !n.read && 'bg-primary/[0.05]')}
                >
                  <span className={cn('mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md',
                    ['paid', 'resolved'].includes(n.kind) ? 'bg-emerald-500/12 text-emerald-600' : 'bg-primary/12 text-primary')}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">{n.title}</span>
                    {n.body && <span className="mt-0.5 block line-clamp-2 text-[12px] text-muted-foreground">{n.body}</span>}
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="block w-full border-t border-border py-2 text-center text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}
